import {_RequestEnvelope, FieldUpdate} from '../core/types';
import {
    AdminGame,
    DisplayInfo,
    GameKind,
    GameTemplateWithStatus,
    GameVariant,
    GameVariantTemplate,
} from '../models/game';

// ---------------------------------------------------------------------------
// CreateGame
// ---------------------------------------------------------------------------

export interface CreateGameRequest {
    RealmId: string;
    TemplateId: string;
    Title: string | null;
    Description: string | null;
    BannerUrl: string | null;
    IconUrl: string | null;
    Images: string[] | null;
    IsActive: boolean | null;
    IsFeatured: boolean | null;
}

export const CreateGameResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    TemplateNotFound: 'TemplateNotFound',
    GameKindNotFound: 'GameKindNotFound',
    GameKindAlreadyUsed: 'GameKindAlreadyUsed',
} as const;
export type CreateGameResult =
    typeof CreateGameResultCode[keyof typeof CreateGameResultCode];

export type CreateGameResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Game: AdminGame;
}
    | {
    Result: 'Unknown' | 'TemplateNotFound' | 'GameKindNotFound' | 'GameKindAlreadyUsed';
    ResultDescription: string;
    Game: null;
};

const _createGameMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'TemplateNotFound',
    3: 'GameKindNotFound',
    4: 'GameKindAlreadyUsed',
};

export function createGame(
    params: CreateGameRequest
): _RequestEnvelope<CreateGameResponse> {
    return {...params, _endpoint: 'admin/game/createGame', _resultCodeMap: _createGameMap};
}

// ---------------------------------------------------------------------------
// CreateGameTemplate
// ---------------------------------------------------------------------------

export interface CreateGameTemplateRequest {
    Title: string;
    Description: string;
    GameUrl: string;
    GameKind: string;
    BannerUrl: string;
    IconUrl: string;
    Images: string[];
    Tags: string[];
    IsActive: boolean;
    IsFeatured: boolean;
    MinEntryFee: number | null;
}

export const CreateGameTemplateResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    GameKindNotFound: 'GameKindNotFound',
} as const;
export type CreateGameTemplateResult =
    typeof CreateGameTemplateResultCode[keyof typeof CreateGameTemplateResultCode];

export type CreateGameTemplateResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Template: GameTemplateWithStatus;
}
    | {
    Result: 'Unknown' | 'GameKindNotFound';
    ResultDescription: string;
    Template: null;
};

const _createGameTemplateMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'GameKindNotFound',
};

export function createGameTemplate(
    params: CreateGameTemplateRequest
): _RequestEnvelope<CreateGameTemplateResponse> {
    return {...params, _endpoint: 'admin/game/createGameTemplate', _resultCodeMap: _createGameTemplateMap};
}

// ---------------------------------------------------------------------------
// CreateGameVariant
// ---------------------------------------------------------------------------

export interface CreateGameVariantRequest {
    RealmId: string;
    GameId: string;
    GameVariantTemplateId: string;
    IsActive: boolean | null;
    DisplayInfo: DisplayInfo | null;
}

export const CreateGameVariantResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    GameNotFound: 'GameNotFound',
} as const;
export type CreateGameVariantResult =
    typeof CreateGameVariantResultCode[keyof typeof CreateGameVariantResultCode];

export type CreateGameVariantResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    GameVariant: GameVariant;
}
    | {
    Result: 'Unknown' | 'GameNotFound';
    ResultDescription: string;
    GameVariant: null;
};

const _createGameVariantMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'GameNotFound',
};

export function createGameVariant(
    params: CreateGameVariantRequest
): _RequestEnvelope<CreateGameVariantResponse> {
    return {...params, _endpoint: 'admin/game/createGameVariant', _resultCodeMap: _createGameVariantMap};
}

// ---------------------------------------------------------------------------
// ListGameKinds
// ---------------------------------------------------------------------------

export interface ListGameKindsResponse {
    GameKinds: GameKind[];
}

export function listGameKinds(): _RequestEnvelope<ListGameKindsResponse> {
    return {_endpoint: 'admin/game/listGameKinds', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListGames
// ---------------------------------------------------------------------------

export interface ListGamesRequest {
    RealmId: string;
}

export interface ListGamesResponse {
    Games: AdminGame[];
}

export function listGames(params: ListGamesRequest): _RequestEnvelope<ListGamesResponse> {
    return {...params, _endpoint: 'admin/game/listGames', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListGameTemplates
// ---------------------------------------------------------------------------

export interface ListGameTemplatesResponse {
    Templates: GameTemplateWithStatus[];
}

export function listGameTemplates(): _RequestEnvelope<ListGameTemplatesResponse> {
    return {_endpoint: 'admin/game/listGameTemplates', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListGameVariants
// ---------------------------------------------------------------------------

export interface ListGameVariantsRequest {
    RealmId: string;
    GameId: string;
}

export const ListGameVariantsResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    GameNotFound: 'GameNotFound',
} as const;
export type ListGameVariantsResult =
    typeof ListGameVariantsResultCode[keyof typeof ListGameVariantsResultCode];

export type ListGameVariantsResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    GameTypes: GameVariant[];
}
    | {
    Result: 'Unknown' | 'GameNotFound';
    ResultDescription: string;
    GameTypes: null;
};

const _listGameVariantsMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'GameNotFound',
};

export function listGameVariants(
    params: ListGameVariantsRequest
): _RequestEnvelope<ListGameVariantsResponse> {
    return {...params, _endpoint: 'admin/game/listGameVariants', _resultCodeMap: _listGameVariantsMap};
}

// ---------------------------------------------------------------------------
// ListGameVariantTemplates
// ---------------------------------------------------------------------------

export interface ListGameVariantTemplatesRequest {
    RealmId: string;
    GameId: string;
}

export const ListGameVariantTemplatesResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    GameNotFound: 'GameNotFound',
} as const;
export type ListGameVariantTemplatesResult =
    typeof ListGameVariantTemplatesResultCode[keyof typeof ListGameVariantTemplatesResultCode];

export type ListGameVariantTemplatesResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Templates: GameVariantTemplate[];
}
    | {
    Result: 'Unknown' | 'GameNotFound';
    ResultDescription: string;
    Templates: null;
};

const _listGameVariantTemplatesMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'GameNotFound',
};

export function listGameVariantTemplates(
    params: ListGameVariantTemplatesRequest
): _RequestEnvelope<ListGameVariantTemplatesResponse> {
    return {...params, _endpoint: 'admin/game/listGameVariantTemplates', _resultCodeMap: _listGameVariantTemplatesMap};
}

// ---------------------------------------------------------------------------
// UpdateGame
// ---------------------------------------------------------------------------

export interface UpdateGameRequest {
    RealmId: string;
    GameId: string;
    Title: FieldUpdate<string | null>;
    Description: FieldUpdate<string | null>;
    BannerUrl: FieldUpdate<string | null>;
    IconUrl: FieldUpdate<string | null>;
    Images: FieldUpdate<string[] | null>;
    IsActive: FieldUpdate<boolean | null>;
    IsFeatured: FieldUpdate<boolean | null>;
}

export const UpdateGameResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    GameKindNotFound: 'GameKindNotFound',
} as const;
export type UpdateGameResult =
    typeof UpdateGameResultCode[keyof typeof UpdateGameResultCode];

export type UpdateGameResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Game: AdminGame;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'GameKindNotFound';
    ResultDescription: string;
    Game: null;
};

const _updateGameMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'GameKindNotFound',
};

export function updateGame(
    params: UpdateGameRequest
): _RequestEnvelope<UpdateGameResponse> {
    return {...params, _endpoint: 'admin/game/updateGame', _resultCodeMap: _updateGameMap};
}

// ---------------------------------------------------------------------------
// UpdateGameTemplate
// ---------------------------------------------------------------------------

export interface UpdateGameTemplateRequest {
    TemplateId: string;
    Title: string | null;
    Description: string | null;
    GameUrl: string | null;
    GameKind: string | null;
    BannerUrl: string | null;
    IconUrl: string | null;
    Images: string[] | null;
    Tags: string[] | null;
    IsActive: boolean | null;
    IsFeatured: boolean | null;
    MinEntryFee: number | null;
    ClearMinEntryFee: boolean;
}

export const UpdateGameTemplateResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    GameKindNotFound: 'GameKindNotFound',
} as const;
export type UpdateGameTemplateResult =
    typeof UpdateGameTemplateResultCode[keyof typeof UpdateGameTemplateResultCode];

export type UpdateGameTemplateResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Template: GameTemplateWithStatus;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'GameKindNotFound';
    ResultDescription: string;
    Template: null;
};

const _updateGameTemplateMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'GameKindNotFound',
};

export function updateGameTemplate(
    params: UpdateGameTemplateRequest
): _RequestEnvelope<UpdateGameTemplateResponse> {
    return {...params, _endpoint: 'admin/game/updateGameTemplate', _resultCodeMap: _updateGameTemplateMap};
}

// ---------------------------------------------------------------------------
// UpdateGameVariant
// ---------------------------------------------------------------------------

export interface UpdateGameVariantRequest {
    RealmId: string;
    GameId: string;
    GameVariantId: string;
    IsActive: FieldUpdate<boolean | null>;
    DisplayInfo: FieldUpdate<DisplayInfo | null>;
}

export const UpdateGameVariantResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    GameNotFound: 'GameNotFound',
    GameVariantNotFound: 'GameVariantNotFound',
} as const;
export type UpdateGameVariantResult =
    typeof UpdateGameVariantResultCode[keyof typeof UpdateGameVariantResultCode];

export type UpdateGameVariantResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    GameVariant: GameVariant;
}
    | {
    Result: 'Unknown' | 'GameNotFound' | 'GameVariantNotFound';
    ResultDescription: string;
    GameVariant: null;
};

const _updateGameVariantMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'GameNotFound',
    3: 'GameVariantNotFound',
};

export function updateGameVariant(
    params: UpdateGameVariantRequest
): _RequestEnvelope<UpdateGameVariantResponse> {
    return {...params, _endpoint: 'admin/game/updateGameVariant', _resultCodeMap: _updateGameVariantMap};
}
